import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { atLeastOneNote } from './cia-validators';

// The form groups of the assessment, one for each step (the four wizard steps of the clinical review), so each step is complete (or not) on its
// own. They are built here and held by the form component so they stay part of its tree and its validity.
// MW-188: the assessment holds clinical judgement only, so there is no member, dependant, consent, preference or referrer group.

// Step 1, Presenting problem: when the intake was, the problem as the member reports it, and its characteristics.
export function createPresentingGroup(formBuilder: FormBuilder) {
  return formBuilder.group({
    intakeDate: ['', Validators.required],
    intakeTime: [''],
    presentingProblemDescription: [''],
    presentingProblemDurationId: [''],
    presentingProblemSeverityId: [''],
    previousEpisodes: [''],
    previousInterventions: [''],
  });
}

// Step 2, Clinical assessment: the 4P grid, the development stages and the patterns.
export function createClinicalBackgroundGroup(formBuilder: FormBuilder) {
  return formBuilder.group({
    fourPFactors: formBuilder.array<AbstractControl>([], atLeastOneNote),
    developmentStages: formBuilder.array<AbstractControl>([]),
    patternRatings: formBuilder.array<AbstractControl>([]),
  });
}

// Step 3, Functional impact and risk: the underlying problems, the work impact, the functional impact, the risk screenings and the risk rating.
export function createFunctionalRiskGroup(formBuilder: FormBuilder) {
  return formBuilder.group({
    underlyingProblemIds: formBuilder.control<string[]>([]),
    workPerformanceImpactRatings: formBuilder.array<AbstractControl>([]),
    functionalImpactSeverityId: [''],
    riskScreenings: formBuilder.array<AbstractControl>([]),
    riskRatingId: [''],
  });
}

// Step 4, Treatment planning: the client's limitations, the P7 planning rows and the clinical impressions and treatment plan.
export function createTreatmentGroup(formBuilder: FormBuilder) {
  return formBuilder.group({
    limitations: formBuilder.array<AbstractControl>([]),
    symptomClusters: formBuilder.array<AbstractControl>([]),
    clinicalImpressions: [''],
    treatmentPlanSummary: [''],
  });
}

// The four parts of the assessment, in the order of the wizard (the wizard shows one at a time).
export type CiaPart = 'presenting' | 'clinical' | 'functional' | 'treatment';

// The typed groups, so the steps and helpers can use their controls without casts.
export type PresentingGroup = ReturnType<typeof createPresentingGroup>;
export type ClinicalBackgroundGroup = ReturnType<typeof createClinicalBackgroundGroup>;
export type FunctionalRiskGroup = ReturnType<typeof createFunctionalRiskGroup>;
export type TreatmentGroup = ReturnType<typeof createTreatmentGroup>;

// What the steps share with the form and with each other that is not part of a group: the selection the last change was made from,
// so a stand-alone choice ("Not applicable") can tell which way the member just changed it.
export type CiaFormState = { previousUnderlyingProblems: string[] };
